/**
 * Disc state machine — 03-disc §2–§7, 02-arena §4 ricochet, 04 §3 shield
 * contact. `stepDiscs` advances every disc and *collects* hurtbox hits into
 * `hits` for batched application (04 §4); it never eliminates directly.
 */
import { gapOf, isOnPad, zoneTeamAt } from './arena.js'
import { pointSegmentDistance, reflect, sweepCircleCircle, sweepCircleSegment, withinArc, type Hit } from './collision.js'
import { canCatch, canPickup, exhaust } from './player.js'
import { discSpeedCap } from './tuning.js'
import type { DiscState, PendingHit, PlayerState, SimState, Team, Pillar } from './types.js'
import { DISC_CLASH_ENABLED, DISC_FLIGHT_Y, FRIENDLY_FIRE, JUMP_CLEAR_MARGIN, SELF_HIT } from './constants.js'

type Contact =
  | { kind: 'surface'; t: number; nx: number; nz: number; surface: 'wall' | 'pillar' | 'corner' }
  | { kind: 'shield'; t: number; nx: number; nz: number; player: PlayerState }
  | { kind: 'hurtbox'; t: number; player: PlayerState }
  | { kind: 'clash'; t: number; other: DiscState }

export function stepDiscs(s: SimState, dt: number, hits: PendingHit[], combatLive: boolean): void {
  // Players already marked as pending-dead this tick can't be hit twice.
  const pendingDead = new Set<string>()
  for (const d of s.discs) {
    d.stateTicks += 1
    switch (d.state) {
      case 'HELD': break // rides with holder (player step)
      case 'FLIGHT': stepFlight(s, d, dt, hits, pendingDead, combatLive); break
      case 'RETURNING': stepReturning(s, d, dt); break
      case 'GROUND': stepGround(s, d, dt); break
      case 'DESPAWN': break
    }
  }
}

// ── FLIGHT ──────────────────────────────────────────────────────────────────

function stepFlight(s: SimState, d: DiscState, dt: number, hits: PendingHit[], pendingDead: Set<string>, combatLive: boolean): void {
  const R = d.tuning.DISC_RADIUS
  let remaining = 1
  for (let iter = 0; iter < 4 && remaining > 1e-9; iter++) {
    const dx = d.vel.x * dt * remaining
    const dz = d.vel.z * dt * remaining
    const c = earliestContact(s, d, dx, dz, R, pendingDead, combatLive)
    if (!c) {
      d.pos.x += dx; d.pos.z += dz
      d.rangeLeft -= Math.hypot(dx, dz)
      break
    }
    // Advance to the contact, spend range for the travelled leg.
    const legX = dx * c.t, legZ = dz * c.t
    d.pos.x += legX; d.pos.z += legZ
    d.rangeLeft -= Math.hypot(legX, legZ)
    remaining *= 1 - c.t

    if (c.kind === 'hurtbox') {
      hits.push({ victimId: c.player.id, attackerId: d.thrower ?? d.homeOwner, discId: d.id, bounceCount: d.bounceCount, sweepT: c.t })
      pendingDead.add(c.player.id)
      continue // disc keeps flying (03 §4: two-for-one)
    }
    if (c.kind === 'clash') {
      const x = d.pos.x, z = d.pos.z
      s.events.push({ type: 'clash', tick: s.tick, discA: d.id, discB: c.other.id, x, z })
      toReturning(s, d, 'clash')
      c.other.pos.x = x; c.other.pos.z = z // both at the contact point (03 §4)
      toReturning(s, c.other, 'clash')
      return
    }
    if (c.kind === 'shield') {
      resolveShieldContact(s, d, c.player, c.nx, c.nz)
      if (d.state !== 'FLIGHT') return
      continue
    }
    // Surface bounce (02 §4).
    const rest = d.tuning.DISC_BOUNCE_RESTITUTION
    if (c.surface === 'corner') {
      d.vel.x = -d.vel.x * rest; d.vel.z = -d.vel.z * rest
    } else {
      const [vx, vz] = reflect(d.vel.x, d.vel.z, c.nx, c.nz, rest)
      d.vel.x = vx; d.vel.z = vz
    }
    d.pos.x += c.nx * 0.01; d.pos.z += c.nz * 0.01
    capSpeed(d)
    d.bounceCount += 1
    s.events.push({ type: 'bounce', tick: s.tick, discId: d.id, surface: c.surface, x: d.pos.x, z: d.pos.z, nx: c.nx, nz: c.nz, bounceCount: d.bounceCount })
    if (d.bounceCount > d.tuning.DISC_MAX_BOUNCES) { toReturning(s, d, 'bounces'); return }
  }
  d.lastZoneTeam = zoneTeamAt(s.arena, d.pos.z) ?? d.lastZoneTeam
  if (d.state !== 'FLIGHT') return
  const speed = Math.hypot(d.vel.x, d.vel.z)
  if (d.rangeLeft <= 0) toReturning(s, d, 'range')
  else if (speed < d.tuning.DISC_MIN_FLIGHT_SPEED) toReturning(s, d, 'slow')
}

function capSpeed(d: DiscState): void {
  const cap = discSpeedCap(d.tuning)
  const sp = Math.hypot(d.vel.x, d.vel.z)
  if (sp > cap) { d.vel.x *= cap / sp; d.vel.z *= cap / sp }
}

/** Spec §1 broadphase: pillars a disc swept by (dx, dz) with radius R could touch this tick. */
export function pillarCandidates(pillars: readonly Pillar[], px: number, pz: number, dx: number, dz: number, R: number): Pillar[] {
  const reach = Math.hypot(dx, dz) + R + 1e-3
  const out: Pillar[] = []
  for (const pl of pillars) if (Math.abs(pl.x - px) <= reach + pl.r && Math.abs(pl.z - pz) <= reach + pl.r) out.push(pl)
  return out
}

function earliestContact(s: SimState, d: DiscState, dx: number, dz: number, R: number, pendingDead: Set<string>, combatLive: boolean): Contact | null {
  let best: Contact | null = null
  const px = d.pos.x, pz = d.pos.z
  const consider = (c: Contact) => { if (!best || c.t < best.t) best = c }

  // Players (03 §4 step 2): shield first, then hurtbox; only enemies of disc.team.
  if (combatLive) {
    for (const p of s.players) {
      if (!p.alive || pendingDead.has(p.id)) continue
      if (!FRIENDLY_FIRE && p.team === d.team) continue
      if (!SELF_HIT && p.id === d.thrower) continue // thrower immune even with friendly fire on (01 §G)
      if (p.invulnUntilTick > s.tick) continue
      const t = p.tuning
      let shieldHit: Hit | null = null
      if (p.state === 'SHIELD' && !d.tuning.DISC_SHIELD_PIERCE) {
        shieldHit = sweepCircleCircle(px, pz, dx, dz, R, p.pos.x, p.pos.z, t.SHIELD_RADIUS)
        if (shieldHit) {
          const cx = px + dx * shieldHit.t, cz = pz + dz * shieldHit.t
          if (!withinArc(p.pos.x, p.pos.z, p.aim.x, p.aim.z, cx, cz, t.SHIELD_ARC_HALF_DEG)) shieldHit = null
        }
      }
      if (shieldHit) { consider({ kind: 'shield', t: shieldHit.t, nx: shieldHit.nx, nz: shieldHit.nz, player: p }); continue }
      if (p.state === 'DASHING' && p.stateTicks <= t.DASH_IFRAME_TICKS && t.DASH_IFRAME_TICKS > 0) continue
      // Jump-dodge (03 §4 step 2, spec §1.4): a flat disc passes under a body whose feet are above its top edge.
      if (p.pos.y > DISC_FLIGHT_Y + R + JUMP_CLEAR_MARGIN) continue
      for (const hb of p.hurtboxes) {
        const h = sweepCircleCircle(px, pz, dx, dz, R, p.pos.x + hb.dx, p.pos.z + hb.dz, hb.r)
        if (h) consider({ kind: 'hurtbox', t: h.t, player: p })
      }
    }
    // Clash (03 §4 step 3): sweep against other FLIGHT discs.
    for (const o of s.discs) {
      if (!DISC_CLASH_ENABLED || o === d || o.state !== 'FLIGHT') continue
      // Other disc treated as static at its current position (good enough at 0.4 m/tick; both sweep).
      const h = sweepCircleCircle(px, pz, dx, dz, R, o.pos.x, o.pos.z, o.tuning.DISC_RADIUS)
      if (h) consider({ kind: 'clash', t: h.t, other: o })
    }
  }
  // Surfaces (02 §4).
  for (const w of s.arena.walls) {
    const h = sweepCircleSegment(px, pz, dx, dz, R, w.ax, w.az, w.bx, w.bz)
    if (h) {
      // Corner rule: within DISC_RADIUS of another wall at the contact → reflect both.
      const cx = px + dx * h.t, cz = pz + dz * h.t
      let corner = false
      for (const w2 of s.arena.walls) if (w2 !== w && pointSegmentDistance(cx, cz, w2.ax, w2.az, w2.bx, w2.bz) <= R * 2 + 1e-6) corner = true
      consider({ kind: 'surface', t: h.t, nx: h.nx, nz: h.nz, surface: corner ? 'corner' : 'wall' })
    }
  }
  for (const pl of pillarCandidates(s.arena.pillars, px, pz, dx, dz, R)) {
    const h = sweepCircleCircle(px, pz, dx, dz, R, pl.x, pl.z, pl.r)
    if (h) consider({ kind: 'surface', t: h.t, nx: h.nx, nz: h.nz, surface: 'pillar' })
  }
  return best
}

/** 04 §3 perfect / late resolution. `nx,nz` = contact normal (player→disc). */
function resolveShieldContact(s: SimState, d: DiscState, p: PlayerState, nx: number, nz: number): void {
  const t = p.tuning
  const speed = Math.hypot(d.vel.x, d.vel.z)
  // A perfect window of 0 (Bulwark, 06 §2) means never perfect, not "perfect on the raise tick".
  if (t.SHIELD_PERFECT_TICKS > 0 && p.shieldAgeTicks <= t.SHIELD_PERFECT_TICKS) {
    const fromId = d.thrower
    const v = speed * t.DEFLECT_PERFECT_SPEED_MULT
    // Disc-scoped tuning re-copied from the new thrower (06 §1).
    for (const k of Object.keys(d.tuning) as (keyof typeof d.tuning)[]) (d.tuning as Record<string, unknown>)[k] = t[k]
    d.vel.x = p.aim.x * v; d.vel.z = p.aim.z * v
    capSpeed(d)
    d.pos.x += nx * 0.01; d.pos.z += nz * 0.01
    d.thrower = p.id
    d.team = p.team
    d.bounceCount = 0
    d.rangeLeft = d.tuning.DISC_RANGE
    p.stamina = Math.min(t.STAMINA_MAX, p.stamina + t.DEFLECT_PERFECT_REFUND)
    p.roundStats.perfectDeflects += 1
    s.events.push({ type: 'deflectPerfect', tick: s.tick, discId: d.id, playerId: p.id, fromId })
  } else {
    const [vx, vz] = reflect(d.vel.x, d.vel.z, nx, nz, t.DEFLECT_LATE_SPEED_MULT)
    d.vel.x = vx; d.vel.z = vz
    d.pos.x += nx * 0.01; d.pos.z += nz * 0.01
    d.bounceCount += 1
    p.stamina -= t.DEFLECT_LATE_COST
    p.roundStats.lateBlocks += 1
    let exhausted = false
    if (p.stamina <= 0) { p.stamina = 0; exhaust(s, p); exhausted = true }
    s.events.push({ type: 'deflectLate', tick: s.tick, discId: d.id, playerId: p.id, exhausted })
    s.events.push({ type: 'bounce', tick: s.tick, discId: d.id, surface: 'shield', x: d.pos.x, z: d.pos.z, nx, nz, bounceCount: d.bounceCount })
    if (d.bounceCount > d.tuning.DISC_MAX_BOUNCES) toReturning(s, d, 'bounces')
  }
}

// ── RETURNING ───────────────────────────────────────────────────────────────

export function toReturning(s: SimState, d: DiscState, reason: 'range' | 'bounces' | 'slow' | 'clash'): void {
  const holder = d.thrower ? s.players.find((p) => p.id === d.thrower) : undefined
  if (!holder || !holder.alive) {
    const target = holder ? nearestLivingTeammate(s, holder.team, holder.id) : undefined
    if (!target) { toGround(s, d, reason === 'clash' ? 'clash' : 'holderDead'); return }
    d.holder = target.id
  } else {
    d.holder = holder.id
  }
  d.state = 'RETURNING'
  d.stateTicks = 0
  d.bounceCount = 0
}

function nearestLivingTeammate(s: SimState, team: Team, exceptId: string, from?: { x: number; z: number }): PlayerState | undefined {
  let best: PlayerState | undefined
  let bd = Infinity
  for (const p of s.players) {
    if (!p.alive || p.team !== team || p.id === exceptId) continue
    const dist = from ? Math.hypot(p.pos.x - from.x, p.pos.z - from.z) : 0
    if (dist < bd) { bd = dist; best = p }
  }
  return best
}

function stepReturning(s: SimState, d: DiscState, dt: number): void {
  let holder = d.holder ? s.players.find((p) => p.id === d.holder) : undefined
  if (!holder || !holder.alive) {
    const team = holder?.team ?? d.team
    const next = nearestLivingTeammate(s, team, holder?.id ?? '', d.pos)
    if (!next) { toGround(s, d, 'holderDead'); return }
    d.holder = next.id
    holder = next
  }
  if (d.stateTicks >= d.tuning.DISC_RETURN_TIMEOUT_TICKS) { toGround(s, d, 'timeout'); return }
  // Homing with a turn-rate cap (03 §5).
  const toX = holder.pos.x - d.pos.x, toZ = holder.pos.z - d.pos.z
  const tl = Math.hypot(toX, toZ)
  const want = tl > 1e-9 ? Math.atan2(toZ, toX) : Math.atan2(d.vel.z, d.vel.x)
  const cur = Math.hypot(d.vel.x, d.vel.z) > 1e-9 ? Math.atan2(d.vel.z, d.vel.x) : want
  let diff = want - cur
  while (diff > Math.PI) diff -= Math.PI * 2
  while (diff < -Math.PI) diff += Math.PI * 2
  const maxTurn = (d.tuning.DISC_RETURN_TURN_RATE * Math.PI / 180) * dt
  const ang = cur + Math.max(-maxTurn, Math.min(maxTurn, diff))
  const sp = d.tuning.DISC_RETURN_SPEED
  d.vel.x = Math.cos(ang) * sp; d.vel.z = Math.sin(ang) * sp
  d.pos.x += d.vel.x * dt; d.pos.z += d.vel.z * dt
  d.pos.y += (DISC_FLIGHT_Y - d.pos.y) * Math.min(1, 10 * dt)
}

/** End-of-tick catch (03 §5): holder first, then living teammates. */
export function resolveCatches(s: SimState): void {
  for (const d of s.discs) {
    if (d.state !== 'RETURNING') continue
    const holder = s.players.find((p) => p.id === d.holder)
    const candidates: PlayerState[] = []
    if (holder) candidates.push(holder)
    const team = holder?.team ?? d.team
    for (const p of s.players) if (p.team === team && p !== holder) candidates.push(p)
    for (const p of candidates) {
      if (!canCatch(p)) continue
      if (Math.hypot(p.pos.x - d.pos.x, p.pos.z - d.pos.z) > p.tuning.CATCH_RADIUS) continue
      const byTeammate = p !== holder
      d.state = 'HELD'; d.stateTicks = 0
      d.holder = p.id; d.team = p.team; d.thrower = null
      d.vel = { x: 0, y: 0, z: 0 }
      d.pos = { x: p.pos.x, y: DISC_FLIGHT_Y, z: p.pos.z }
      p.discsHeld.push(d.id)
      p.roundStats.catches += 1
      s.events.push({ type: 'catch', tick: s.tick, discId: d.id, playerId: p.id, byTeammate })
      break
    }
  }
}

// ── GROUND ──────────────────────────────────────────────────────────────────

export function toGround(s: SimState, d: DiscState, reason: 'range' | 'bounces' | 'slow' | 'clash' | 'timeout' | 'holderDead' | 'drop'): void {
  d.state = 'GROUND'
  d.stateTicks = 0
  d.holder = null
  d.vel.x *= 0.25; d.vel.z *= 0.25; d.vel.y = 0
  d.pos.y = 0
  if (reason === 'drop') { d.vel.x = 0; d.vel.z = 0 }
  d.lastZoneTeam = zoneTeamAt(s.arena, d.pos.z) ?? d.lastZoneTeam
  settleOffVoid(s, d)
  s.events.push({ type: 'ground', tick: s.tick, discId: d.id, x: d.pos.x, z: d.pos.z, reason })
}

/** 03 §6: a ground disc never rests over void — snap to the nearest pad edge
 *  on the side of the zone it was last over. */
function settleOffVoid(s: SimState, d: DiscState): void {
  if (isOnPad(s.arena, d.pos.x, d.pos.z)) return
  const r = d.tuning.DISC_RADIUS
  const gap = gapOf(s.arena)
  const side: Team = d.lastZoneTeam ?? (d.pos.z < (gap.start + gap.end) / 2 ? 0 : 1)
  d.pos.z = side === 0 ? gap.start - r : gap.end + r
  d.vel.x = 0; d.vel.z = 0
}

function stepGround(s: SimState, d: DiscState, dt: number): void {
  const sp = Math.hypot(d.vel.x, d.vel.z)
  if (sp > 1e-6) {
    const ns = Math.max(0, sp - d.tuning.DISC_GROUND_SLIDE_DECEL * dt)
    d.vel.x *= ns / sp; d.vel.z *= ns / sp
    d.pos.x += d.vel.x * dt; d.pos.z += d.vel.z * dt
    const hw = s.arena.width / 2 - d.tuning.DISC_RADIUS
    if (d.pos.x < -hw) { d.pos.x = -hw; d.vel.x = 0 }
    if (d.pos.x > hw) { d.pos.x = hw; d.vel.x = 0 }
    const zr = d.tuning.DISC_RADIUS
    if (d.pos.z < zr) { d.pos.z = zr; d.vel.z = 0 }
    if (d.pos.z > s.arena.length - zr) { d.pos.z = s.arena.length - zr; d.vel.z = 0 }
    const zt = zoneTeamAt(s.arena, d.pos.z)
    if (zt !== null) d.lastZoneTeam = zt
    settleOffVoid(s, d)
  }
  if (d.stateTicks >= d.tuning.DISC_GROUND_TIMEOUT_TICKS) {
    const home = s.players.find((p) => p.id === d.homeOwner)
    if (d.despawnOnTimeout || !home || !home.alive) { despawn(s, d); return }
    if (home.discsHeld.length >= home.maxDiscs) { d.stateTicks = 0; return }
    d.state = 'HELD'; d.stateTicks = 0
    d.holder = home.id; d.team = home.team; d.thrower = null
    d.vel = { x: 0, y: 0, z: 0 }
    d.pos = { x: home.pos.x, y: DISC_FLIGHT_Y, z: home.pos.z }
    home.discsHeld.push(d.id)
    s.events.push({ type: 'rematerialise', tick: s.tick, discId: d.id, playerId: home.id })
  }
}

/** End-of-tick pickup (03 §6): any team, free slot, not dashing. Ties → lowest player index. */
export function resolvePickups(s: SimState): void {
  for (const d of s.discs) {
    if (d.state !== 'GROUND') continue
    for (const p of s.players) {
      if (!canPickup(p)) continue
      if (Math.hypot(p.pos.x - d.pos.x, p.pos.z - d.pos.z) > p.tuning.PICKUP_RADIUS) continue
      const stolen = p.team !== d.team
      d.state = 'HELD'; d.stateTicks = 0
      d.holder = p.id; d.team = p.team; d.thrower = null
      d.vel = { x: 0, y: 0, z: 0 }
      d.pos = { x: p.pos.x, y: DISC_FLIGHT_Y, z: p.pos.z }
      p.discsHeld.push(d.id)
      if (stolen) p.roundStats.steals += 1
      s.events.push({ type: 'pickup', tick: s.tick, discId: d.id, playerId: p.id, stolen })
      break
    }
  }
}

export function despawn(s: SimState, d: DiscState): void {
  if (d.state === 'DESPAWN') return
  d.state = 'DESPAWN'
  d.holder = null
  s.events.push({ type: 'despawn', tick: s.tick, discId: d.id })
}

/** Remove DESPAWN discs at end of tick. */
export function sweepDespawned(s: SimState): void {
  if (s.discs.some((d) => d.state === 'DESPAWN')) s.discs = s.discs.filter((d) => d.state !== 'DESPAWN')
}

/** Eliminated player drops held discs (03 §8). */
export function dropHeldDiscs(s: SimState, p: PlayerState): void {
  for (const id of p.discsHeld) {
    const d = s.discs.find((x) => x.id === id)
    if (d) { d.pos = { x: p.pos.x, y: 0, z: p.pos.z }; toGround(s, d, 'drop') }
  }
  p.discsHeld = []
}
